import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { PageHeader, Panel, Loading, StatusBadge, Tag } from "@/components/common";
import { MusicNotes, WaveSine, LockKey, Sparkle, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";

const THEMES = [
  { name: "Sonic Logo", note: "2-3s signature sting" },
  { name: "Main Theme", note: "Full brand theme" },
  { name: "Happy Theme", note: "Upbeat & playful" },
  { name: "Adventure Theme", note: "Driving & heroic" },
  { name: "Gentle/Sad Theme", note: "Soft & emotional" },
  { name: "Mystery Theme", note: "Curious & suspenseful" },
  { name: "Victory Theme", note: "Triumphant" },
  { name: "Bedtime Theme", note: "Calm lullaby" },
  { name: "Outro Theme", note: "End-card music" },
];

export default function SonicIdentity() {
  const { data: studio } = useFetch("/studios/current");
  const [musicStatus, setMusicStatus] = useState(null);
  const [busy, setBusy] = useState(null);
  const [official, setOfficial] = useState({});

  useEffect(() => {
    api.get("/local/status").then(({ data }) => setMusicStatus(data.components.find((c) => c.component === "music")));
  }, []);

  const generate = async (name) => {
    setBusy(name);
    try {
      const { data } = await api.post("/local/test/music");
      if (data.ok) toast.success(`Generated ${name} (local)`);
      else toast.error(`${name}: ${data.status} — ${data.message}`);
    } catch { toast.error("Local music engine unavailable"); }
    finally { setBusy(null); }
  };

  if (!studio || !musicStatus) return <Loading />;
  const ready = musicStatus.status === "LOCAL READY" || musicStatus.status === "LOCAL RUNNING";

  return (
    <div>
      <PageHeader icon={WaveSine} title="Sonic Identity Studio"
        subtitle={`Each studio keeps its own recognisable musical identity. ${studio.name} reuses its official melody/style across episodes instead of inventing a new theme each time.`}
        badge={<StatusBadge status={ready ? "LIVE" : "NOT CONNECTED"} />} />

      <div className="mb-6 flex items-start gap-3 rounded-xl border border-[#8B5CF6]/30 bg-[#8B5CF6]/8 px-4 py-3">
        <MusicNotes size={20} weight="duotone" className="text-[#8B5CF6] mt-0.5" />
        <div>
          <div className="text-sm text-white font-medium">Music engine: {musicStatus.model} · <span className="font-mono text-xs">{musicStatus.status}</span></div>
          <p className="text-sm text-zinc-400">{musicStatus.detail} API cost: {musicStatus.api_cost}. Licence: {musicStatus.license} ({musicStatus.commercial_use}).</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {THEMES.map((t) => (
          <Panel key={t.name} data-testid={`theme-${t.name.replace(/[^a-zA-Z]/g, "")}`} className="flex flex-col">
            <div className="flex items-center justify-between mb-1">
              <span className="font-display font-bold text-white">{t.name}</span>
              {official[t.name] && <Tag className="text-[10px] text-[#00FF9D] border-[#00FF9D]/40">Official</Tag>}
            </div>
            <p className="text-xs text-zinc-500 flex-1">{t.note}</p>
            <div className="mt-4 flex gap-2">
              <button onClick={() => generate(t.name)} disabled={busy === t.name}
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-white/5 border border-white/10 py-2 text-xs text-white hover:border-[#00E5FF]/40 transition-colors disabled:opacity-60">
                {busy === t.name ? <Spinner size={13} className="animate-spin" /> : <Sparkle size={13} weight="fill" className="text-[#00E5FF]" />} Generate Local
              </button>
              <button onClick={() => { setOfficial((o) => ({ ...o, [t.name]: true })); toast.success(`${t.name} locked as official sonic identity`); }}
                className="inline-flex items-center justify-center rounded-full bg-[#00FF9D]/10 border border-[#00FF9D]/40 text-[#00FF9D] px-3 py-2 text-xs hover:bg-[#00FF9D]/20 transition-colors">
                <LockKey size={13} weight="fill" />
              </button>
            </div>
          </Panel>
        ))}
      </div>
      <p className="mt-6 text-xs text-zinc-500">"Lock as Official Sonic Identity" stores the theme with licensing/provenance. Generation runs on the local ACE-Step engine once a GPU / self-hosted endpoint is attached (Settings → Local AI).</p>
    </div>
  );
}
